import { Howl, Howler } from 'howler';

/**
 * Audio Manager using Howler.js
 * Handles all game audio including music, sound effects, and spatial audio
 */
export class AudioManager {
    constructor() {
        this.sounds = new Map();
        this.music = null;
        this.masterVolume = 1.0;
        this.sfxVolume = 1.0;
        this.musicVolume = 0.5;
        
        // Initialize Howler
        Howler.volume(this.masterVolume);
    }
    
    /**
     * Load a sound effect
     * @param {string} name - Name identifier for the sound
     * @param {string|Array} src - Sound file path(s)
     * @param {Object} options - Howler options
     */
    loadSound(name, src, options = {}) {
        const sound = new Howl({
            src: Array.isArray(src) ? src : [src],
            volume: options.volume || this.sfxVolume,
            loop: options.loop || false,
            ...options
        });
        
        this.sounds.set(name, sound);
        return sound;
    }
    
    /**
     * Play a sound effect
     * @param {string} name - Sound name
     * @param {Object} options - Play options (volume, position, etc.)
     */
    playSound(name, options = {}) {
        const sound = this.sounds.get(name);
        if (!sound) {
            console.warn(`Sound "${name}" not found`);
            return null;
        }
        
        const id = sound.play();
        
        if (options.volume !== undefined) {
            sound.volume(options.volume * this.sfxVolume, id);
        }
        
        if (options.position) {
            sound.pos(options.position.x, options.position.y, options.position.z, id);
        }
        
        if (options.loop) {
            sound.loop(true, id);
        }
        
        return id;
    }
    
    /**
     * Stop a sound
     * @param {string} name - Sound name
     * @param {number} id - Sound ID (optional)
     */
    stopSound(name, id = null) {
        const sound = this.sounds.get(name);
        if (sound) {
            if (id !== null) {
                sound.stop(id);
            } else {
                sound.stop();
            }
        }
    }
    
    /**
     * Load and play background music
     * @param {string|Array} src - Music file path(s)
     * @param {Object} options - Music options
     */
    playMusic(src, options = {}) {
        if (this.music) {
            this.music.stop();
        }
        
        this.music = new Howl({
            src: Array.isArray(src) ? src : [src],
            volume: (options.volume || this.musicVolume) * this.masterVolume,
            loop: options.loop !== false,
            ...options
        });
        
        this.music.play();
        return this.music;
    }
    
    /**
     * Stop background music
     */
    stopMusic() {
        if (this.music) {
            this.music.stop();
            this.music = null;
        }
    }
    
    /**
     * Set master volume
     * @param {number} volume - Volume (0.0 to 1.0)
     */
    setMasterVolume(volume) {
        this.masterVolume = Math.max(0, Math.min(1, volume));
        Howler.volume(this.masterVolume);
    }
    
    /**
     * Set SFX volume
     * @param {number} volume - Volume (0.0 to 1.0)
     */
    setSFXVolume(volume) {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        // Update all sounds
        this.sounds.forEach(sound => {
            sound.volume(this.sfxVolume);
        });
    }
    
    /**
     * Set music volume
     * @param {number} volume - Volume (0.0 to 1.0)
     */
    setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        if (this.music) {
            this.music.volume(this.musicVolume * this.masterVolume);
        }
    }
    
    /**
     * Update spatial audio for 3D positioning
     * @param {string} name - Sound name
     * @param {number} id - Sound ID
     * @param {Object} position - 3D position
     * @param {Object} listenerPosition - Listener position
     */
    updateSpatialAudio(name, id, position, listenerPosition) {
        const sound = this.sounds.get(name);
        if (sound && id !== null) {
            const distance = Math.sqrt(
                Math.pow(position.x - listenerPosition.x, 2) +
                Math.pow(position.y - listenerPosition.y, 2) +
                Math.pow(position.z - listenerPosition.z, 2)
            );
            
            // Distance-based volume (max distance 50 units)
            const maxDistance = 50;
            const volume = Math.max(0, 1 - (distance / maxDistance));
            sound.volume(volume * this.sfxVolume, id);
            
            sound.pos(position.x, position.y, position.z, id);
        }
    }
    
    /**
     * Preload common game sounds
     */
    preloadCommonSounds() {
        // These would be actual sound files in production
        // For now, we'll create placeholder sounds
        
        // Gun sounds
        this.loadSound('gun_shot', ['/sounds/gun_shot.mp3'], { volume: 0.7 });
        this.loadSound('gun_reload', ['/sounds/gun_reload.mp3'], { volume: 0.5 });
        this.loadSound('gun_empty', ['/sounds/gun_empty.mp3'], { volume: 0.6 });
        
        // Impact sounds
        this.loadSound('hit_enemy', ['/sounds/hit_enemy.mp3'], { volume: 0.8 });
        this.loadSound('hit_wall', ['/sounds/hit_wall.mp3'], { volume: 0.5 });
        
        // Blood/gore sounds
        this.loadSound('blood_splat', ['/sounds/blood_splat.mp3'], { volume: 0.7 });
        this.loadSound('death_scream', ['/sounds/death_scream.mp3'], { volume: 0.8 });
        
        // Enemy sounds
        this.loadSound('enemy_attack', ['/sounds/enemy_attack.mp3'], { volume: 0.6 });
        
        // Footsteps
        this.loadSound('footstep', ['/sounds/footstep.mp3'], { volume: 0.3 });
        
        // UI sounds
        this.loadSound('ui_click', ['/sounds/ui_click.mp3'], { volume: 0.5 });
        this.loadSound('ui_hover', ['/sounds/ui_hover.mp3'], { volume: 0.3 });
    }
    
    /**
     * Cleanup
     */
    destroy() {
        this.sounds.forEach(sound => sound.unload());
        this.sounds.clear();
        
        if (this.music) {
            this.music.unload();
            this.music = null;
        }
        
        Howler.unload();
    }
}

// Singleton instance
export const audioManager = new AudioManager();

